/**
 * ShareEventModal Component
 * Modal for adding users to an event with specific permission levels
 */

import React, { useState, useEffect, useRef } from "react";
import {
  XMarkIcon,
  MagnifyingGlassIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";
import {
  useSearchUsersForSharing,
  useAddEventPermission,
} from "../../hooks/useEventPermissions";
import { PermissionLevel } from "../../types/event.types";
import type { UUID } from "../../types/common.types";
import Button from "../common/Button";
import { useToast } from "../ui/Toast";
import { preventEnterSubmit } from "../../utils/preventEnterSubmit";

interface ShareEventModalProps {
  eventId: UUID;
  onClose: () => void;
  onSuccess: () => void;
}

const ShareEventModal: React.FC<ShareEventModalProps> = ({
  eventId,
  onClose,
  onSuccess,
}) => {
  const { showToast } = useToast();
  const inputRef = useRef<HTMLInputElement>(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUser, setSelectedUser] = useState<{
    id: UUID;
    full_name: string;
    email: string;
  } | null>(null);
  const [permissionLevel, setPermissionLevel] = useState<PermissionLevel>(
    PermissionLevel.EDIT,
  );

  // Search users
  const { data: searchResults, isLoading: isSearching } =
    useSearchUsersForSharing(eventId, searchQuery);

  // Add permission mutation
  const addPermission = useAddEventPermission();

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedUser) {
      showToast("error", "Please select a user");
      return;
    }

    try {
      await addPermission.mutateAsync({
        eventId,
        userId: selectedUser.id,
        permissionLevel,
      });
      showToast("success", `${selectedUser.full_name} added to event`);
      onSuccess();
    } catch (error: any) {
      showToast("error", error.message || "Failed to add user");
    }
  };

  const handleUserSelect = (user: typeof selectedUser) => {
    setSelectedUser(user);
    setSearchQuery("");
  };

  const clearSelection = () => {
    setSelectedUser(null);
    inputRef.current?.focus();
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Share Event</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 p-1 rounded-md hover:bg-gray-100"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <form onSubmit={handleSubmit} className="p-4 space-y-4">
            {/* User Search/Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                User
              </label>

              {selectedUser ? (
                /* Selected User Display */
                <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <div className="flex items-center space-x-3">
                    <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
                      <span className="text-primary-700 font-medium text-sm">
                        {selectedUser.full_name.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 text-sm">
                        {selectedUser.full_name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {selectedUser.email}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={clearSelection}
                    className="text-gray-400 hover:text-gray-600 p-1"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                /* Search Input */
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    ref={inputRef}
                    type="text"
                    value={searchQuery}
                    onKeyDown={preventEnterSubmit}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by name or email..."
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg
                      focus:ring-2 focus:ring-primary-500 focus:border-primary-500
                      placeholder-gray-400 text-sm"
                  />

                  {/* Search Results Dropdown */}
                  {searchQuery.length >= 2 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                      {isSearching ? (
                        <div className="p-4 text-center text-gray-500 text-sm">
                          Searching...
                        </div>
                      ) : searchResults?.length === 0 ? (
                        <div className="p-4 text-center text-gray-500 text-sm">
                          No users found
                        </div>
                      ) : (
                        searchResults?.map((user) => (
                          <button
                            key={user.id}
                            type="button"
                            onClick={() => handleUserSelect(user)}
                            className="w-full flex items-center space-x-3 p-3 hover:bg-gray-50 text-left"
                          >
                            <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
                              <span className="text-primary-700 font-medium text-sm">
                                {user.full_name.charAt(0)}
                              </span>
                            </div>
                            <div>
                              <p className="font-medium text-gray-900 text-sm">
                                {user.full_name}
                              </p>
                              <p className="text-xs text-gray-500">
                                {user.email}
                              </p>
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Permission Level */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Permission Level
              </label>
              <div className="space-y-2">
                {[
                  {
                    level: PermissionLevel.OWNER,
                    label: "Owner",
                    description: "Can edit, share, and manage other owners",
                  },
                  {
                    level: PermissionLevel.EDIT,
                    label: "Editor",
                    description: "Can edit event details",
                  },
                  {
                    level: PermissionLevel.VIEW,
                    label: "Viewer",
                    description: "Can only view event details",
                  },
                ].map(({ level, label, description }) => (
                  <label
                    key={level}
                    className={`
                      flex items-center p-3 border rounded-lg cursor-pointer transition-colors
                      ${
                        permissionLevel === level
                          ? "border-primary-500 bg-primary-50"
                          : "border-gray-200 hover:border-gray-300"
                      }
                    `}
                  >
                    <input
                      type="radio"
                      name="permissionLevel"
                      value={level}
                      checked={permissionLevel === level}
                      onChange={() => setPermissionLevel(level)}
                      className="sr-only"
                    />
                    <div className="flex-1">
                      <p className="font-medium text-gray-900 text-sm">
                        {label}
                      </p>
                      <p className="text-xs text-gray-500">{description}</p>
                    </div>
                    {permissionLevel === level && (
                      <CheckIcon className="h-5 w-5 text-primary-600" />
                    )}
                  </label>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <Button type="button" variant="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={!selectedUser || addPermission.isPending}
                isLoading={addPermission.isPending}
              >
                Add User
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ShareEventModal;
